import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  DocumentChartBarIcon,
  CalendarIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { DateInput } from '@/components/ui/DateInput';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { cn } from '@/utils/cn';

interface ReportFilters {
  startDate: string;
  endDate: string;
  threatLevels?: string[];
  categories?: string[];
  statuses?: string[];
}

interface GenerateReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (filters: ReportFilters, format: string, title: string) => Promise<void>;
}

const datePresets = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
  { label: 'Last 6 months', days: 180 },
  { label: 'Last year', days: 365 },
];

const threatLevelOptions = [
  { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-800' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-800' },
  { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-800' },
  { value: 'low', label: 'Low', color: 'bg-green-100 text-green-800' },
];

const categoryOptions = [
  { value: 'cyber', label: 'Cyber' },
  { value: 'physical', label: 'Physical' },
  { value: 'financial', label: 'Financial' },
  { value: 'reputational', label: 'Reputational' },
  { value: 'operational', label: 'Operational' },
];

const statusOptions = [
  { value: 'published', label: 'Published' },
  { value: 'review', label: 'Review' },
  { value: 'draft', label: 'Draft' },
  { value: 'archived', label: 'Archived' },
];

const formatOptions = [
  { value: 'pdf', label: 'PDF', description: 'Professional report format' },
  { value: 'excel', label: 'Excel', description: 'Spreadsheet with multiple sheets' },
  { value: 'csv', label: 'CSV', description: 'Simple data export' },
];

export default function GenerateReportModal({
  isOpen,
  onClose,
  onGenerate,
}: GenerateReportModalProps) {
  const [title, setTitle] = useState('');
  const [datePreset, setDatePreset] = useState('30');
  const [useCustomDates, setUseCustomDates] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedThreatLevels, setSelectedThreatLevels] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(['published']);
  const [format, setFormat] = useState('pdf');
  const [isGenerating, setIsGenerating] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (!isGenerating) {
      setTitle('');
      setDatePreset('30');
      setUseCustomDates(false);
      setStartDate('');
      setEndDate('');
      setSelectedThreatLevels([]);
      setSelectedCategories([]);
      setSelectedStatuses(['published']);
      setFormat('pdf');
      setSuccess(false);
      setError(null);
      onClose();
    }
  };

  const handleGenerate = async () => {
    setError(null);

    // Validate
    if (!title.trim()) {
      setError('Please enter a report title');
      return;
    }

    let finalStartDate: string;
    let finalEndDate: string;

    if (useCustomDates) {
      if (!startDate || !endDate) {
        setError('Please select both start and end dates');
        return;
      }
      if (new Date(startDate) >= new Date(endDate)) {
        setError('Start date must be before end date');
        return;
      }
      finalStartDate = startDate;
      finalEndDate = endDate;
    } else {
      const days = parseInt(datePreset);
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - days);
      finalStartDate = start.toISOString().split('T')[0];
      finalEndDate = end.toISOString().split('T')[0];
    }

    const filters: ReportFilters = {
      startDate: finalStartDate,
      endDate: finalEndDate,
      threatLevels: selectedThreatLevels.length > 0 ? selectedThreatLevels : undefined,
      categories: selectedCategories.length > 0 ? selectedCategories : undefined,
      statuses: selectedStatuses.length > 0 ? selectedStatuses : undefined,
    };

    setIsGenerating(true);
    try {
      await onGenerate(filters, format, title);
      setSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      setError((err as Error).message || 'Failed to generate report');
    } finally {
      setIsGenerating(false);
    }
  };

  const toggleSelection = (
    value: string,
    selected: string[],
    setter: (values: string[]) => void
  ) => {
    if (selected.includes(value)) {
      setter(selected.filter((v) => v !== value));
    } else {
      setter([...selected, value]);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm p-4 pt-8"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-3xl max-h-[90vh] overflow-y-auto"
          >
            <Card className="border-slate-200 shadow-xl">
              <CardHeader className="border-b border-slate-200 bg-gradient-to-r from-primary-50 to-primary-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-600">
                      <DocumentChartBarIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-xl text-slate-900">
                        Generate Threat Intelligence Report
                      </CardTitle>
                      <p className="text-sm text-slate-600 mt-1">
                        Create a comprehensive report with filters
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleClose}
                    disabled={isGenerating}
                    className="rounded-lg p-2 hover:bg-slate-200 transition-colors disabled:opacity-50"
                  >
                    <XMarkIcon className="h-5 w-5 text-slate-600" />
                  </button>
                </div>
              </CardHeader>

              <CardContent className="p-6 space-y-6">
                {success ? (
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="flex flex-col items-center justify-center py-8"
                  >
                    <CheckCircleIcon className="h-16 w-16 text-green-600 mb-4" />
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">
                      Report Generated Successfully!
                    </h3>
                    <p className="text-slate-600">Redirecting...</p>
                  </motion.div>
                ) : (
                  <>
                    {/* Report Title */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Report Title *
                      </label>
                      <Input
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="e.g., Q4 2025 Threat Intelligence Report"
                        disabled={isGenerating}
                        className="w-full"
                      />
                    </div>

                    {/* Date Range */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        <CalendarIcon className="h-4 w-4 inline mr-1" />
                        Date Range *
                      </label>

                      <div className="flex items-center space-x-2 mb-3">
                        <button
                          onClick={() => setUseCustomDates(false)}
                          disabled={isGenerating}
                          className={cn(
                            'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                            !useCustomDates
                              ? 'bg-primary-600 text-white'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                          )}
                        >
                          Presets
                        </button>
                        <button
                          onClick={() => setUseCustomDates(true)}
                          disabled={isGenerating}
                          className={cn(
                            'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                            useCustomDates
                              ? 'bg-primary-600 text-white'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                          )}
                        >
                          Custom Range
                        </button>
                      </div>

                      {!useCustomDates ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {datePresets.map((preset) => (
                            <button
                              key={preset.days}
                              onClick={() => setDatePreset(String(preset.days))}
                              disabled={isGenerating}
                              className={cn(
                                'px-4 py-2 rounded-lg text-sm font-medium transition-colors border',
                                datePreset === String(preset.days)
                                  ? 'bg-primary-50 border-primary-600 text-primary-700'
                                  : 'bg-white border-slate-200 text-slate-700 hover:border-primary-300'
                              )}
                            >
                              {preset.label}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <DateInput
                              label="Start Date"
                              value={startDate}
                              onChange={setStartDate}
                              disabled={isGenerating}
                              className="w-full"
                            />
                          </div>
                          <div>
                            <DateInput
                              label="End Date"
                              value={endDate}
                              onChange={setEndDate}
                              disabled={isGenerating}
                              className="w-full"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Additional Filters */}
                    <div className="border-t border-slate-200 pt-4">
                      <h4 className="text-sm font-medium text-slate-700 mb-3">
                        Additional Filters (Optional)
                      </h4>

                      {/* Threat Levels */}
                      <div className="mb-4">
                        <label className="block text-xs text-slate-600 mb-2">
                          Threat Levels
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {threatLevelOptions.map((option) => (
                            <button
                              key={option.value}
                              onClick={() =>
                                toggleSelection(
                                  option.value,
                                  selectedThreatLevels,
                                  setSelectedThreatLevels
                                )
                              }
                              disabled={isGenerating}
                              className={cn(
                                'px-3 py-1.5 rounded-full text-xs font-medium transition-all border',
                                selectedThreatLevels.includes(option.value)
                                  ? option.color + ' border-transparent'
                                  : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                              )}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Categories */}
                      <div className="mb-4">
                        <label className="block text-xs text-slate-600 mb-2">
                          Categories
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {categoryOptions.map((option) => (
                            <button
                              key={option.value}
                              onClick={() =>
                                toggleSelection(
                                  option.value,
                                  selectedCategories,
                                  setSelectedCategories
                                )
                              }
                              disabled={isGenerating}
                              className={cn(
                                'px-3 py-1.5 rounded-full text-xs font-medium transition-all border',
                                selectedCategories.includes(option.value)
                                  ? 'bg-primary-100 text-primary-800 border-primary-300'
                                  : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                              )}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Statuses */}
                      <div>
                        <label className="block text-xs text-slate-600 mb-2">
                          Statuses
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {statusOptions.map((option) => (
                            <button
                              key={option.value}
                              onClick={() =>
                                toggleSelection(
                                  option.value,
                                  selectedStatuses,
                                  setSelectedStatuses
                                )
                              }
                              disabled={isGenerating}
                              className={cn(
                                'px-3 py-1.5 rounded-full text-xs font-medium transition-all border',
                                selectedStatuses.includes(option.value)
                                  ? 'bg-accent-100 text-accent-800 border-accent-300'
                                  : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                              )}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Format Selection */}
                    <div className="border-t border-slate-200 pt-4">
                      <label className="block text-sm font-medium text-slate-700 mb-3">
                        Report Format *
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {formatOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => setFormat(option.value)}
                            disabled={isGenerating}
                            className={cn(
                              'p-4 rounded-lg border-2 transition-all text-left',
                              format === option.value
                                ? 'border-primary-600 bg-primary-50'
                                : 'border-slate-200 hover:border-primary-300 bg-white'
                            )}
                          >
                            <div className="font-medium text-slate-900">
                              {option.label}
                            </div>
                            <div className="text-xs text-slate-600 mt-1">
                              {option.description}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
                        <XMarkIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-red-800">{error}</div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200">
                      <Button
                        onClick={handleClose}
                        disabled={isGenerating}
                        variant="outline"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleGenerate}
                        disabled={isGenerating}
                        className="bg-primary-600 hover:bg-primary-700"
                      >
                        {isGenerating ? (
                          <>
                            <svg
                              className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              />
                            </svg>
                            Generating...
                          </>
                        ) : (
                          <>
                            <DocumentChartBarIcon className="h-4 w-4 mr-2" />
                            Generate Report
                          </>
                        )}
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
